import { useCallback, useMemo, useState } from 'react';
import { useVacationApprovalMutations } from '@/features/Vacations/hooks/data';
import { useDecideDayOff } from '@/features/DpRequests/hooks/data/useDpRequestMutations';
import type {
    ApprovalDecisionKind,
    ApprovalDecisionSubmission,
    ApprovalItem,
    ApprovalSortOrder,
    ApprovalTypeFilter,
} from '../approvals.types';
import {
    countPendingByKind,
    filterApprovals,
    findOldestPending,
    sortApprovals,
} from '../utils/approvalQueue';
import { useApprovalItems } from './useApprovalItems';

interface ModalState {
    item: ApprovalItem | null;
    kind: ApprovalDecisionKind;
}

export function useApprovalsQueue(managerEmail?: string) {
    const { items, isLoading, errors } = useApprovalItems(managerEmail);
    const { decideRequest } = useVacationApprovalMutations();
    const decideDayOff = useDecideDayOff();
    const [search, setSearch] = useState('');
    const [sortOrder, setSortOrder] = useState<ApprovalSortOrder>('newest');
    const [typeFilter, setTypeFilter] = useState<ApprovalTypeFilter>('all');
    const [modal, setModal] = useState<ModalState>({ item: null, kind: 'approve' });

    const found = useMemo(
        () => filterApprovals(items, search, typeFilter),
        [items, search, typeFilter]
    );

    const pendingItems = useMemo(
        () => sortApprovals(found.filter((item) => item.isPending), sortOrder),
        [found, sortOrder]
    );

    const decidedItems = useMemo(
        () => sortApprovals(found.filter((item) => !item.isPending), sortOrder),
        [found, sortOrder]
    );

    const pendingByKind = useMemo(() => countPendingByKind(items), [items]);
    const oldestPendingAt = useMemo(() => findOldestPending(items), [items]);

    const openDecision = useCallback(
        (item: ApprovalItem, kind: ApprovalDecisionKind) => setModal({ item, kind }),
        []
    );

    const closeDecision = useCallback(
        () => setModal((current) => ({ ...current, item: null })),
        []
    );

    const confirmDecision = useCallback(
        (item: ApprovalItem, decision: ApprovalDecisionSubmission) => {
            if (!managerEmail) return;
            const onSuccess = () => setModal((current) => ({ ...current, item: null }));

            if (item.kind === 'vacation') {
                const { request } = item.item;
                decideRequest.mutate(
                    {
                        requestId: request.id,
                        kind: modal.kind,
                        note: decision.note,
                        managerEmail,
                        sellStatus: request.sellRequest.status,
                        collaboratorEmail: request.collaboratorEmail,
                        collaboratorId: request.collaboratorId,
                        correctionRevision: request.correctionRevision ?? undefined,
                    },
                    { onSuccess }
                );
                return;
            }

            decideDayOff.mutate(
                {
                    requestId: item.request.id,
                    action: modal.kind,
                    note: decision.note || undefined,
                },
                { onSuccess }
            );
        },
        [modal.kind, managerEmail, decideRequest, decideDayOff]
    );

    return {
        pendingItems,
        decidedItems,
        isLoading,
        errors,
        pendingCount: pendingByKind.all,
        pendingByKind,
        decidedCount: items.length - pendingByKind.all,
        oldestPendingAt,
        search,
        setSearch,
        sortOrder,
        setSortOrder,
        typeFilter,
        setTypeFilter,
        decisionItem: modal.item,
        decisionKind: modal.kind,
        openDecision,
        closeDecision,
        confirmDecision,
        isDeciding: decideRequest.isPending || decideDayOff.isPending,
    };
}
